// Internal: rendered exclusively by AccountPane based on account type. Not exported from the panes barrel.
import { getLocalTimeZone } from '@internationalized/date';
import * as React from 'react';

import ComingSoonCard from '../../components/accounts/ComingSoonCard';
import CurrentValueCard from '../../components/accounts/CurrentValueCard';
import ValueOverTimeChart from '../../components/charts/ValueOverTimeChart';
import Entity from '../../components/entity';
import AccountPaneHeader from '../../components/headers/AccountPaneHeader';
import { InvestmentAccountSummary } from '../../components/summaries';
import { useInvestmentActivityParam } from '../../components/tables/hooks/useTableSearchParams';
import InvestmentTransactionsTable from '../../components/tables/InvestmentTransactionsTable';
import { useFinancialAccountBalanceAt } from '../../hooks/queries/useFinancialAccountBalanceAt';
import { useFinancialAccountBalanceHistory } from '../../hooks/queries/useFinancialAccountBalanceHistory';
import { useDateRangeParams } from '../../providers/DateRangeProvider';
import { changeBetween, previousDayCutoff, sinceDayLabel } from '../../utils/account/balanceHistory';
import { hasRequiredDistributions } from '../../utils/account/investmentTaxTreatment';

import type { GetFinancialAccountQuery } from './__generated__/AccountPane.gql';

interface IInvestmentAccountPaneProps {
  financialAccount: NonNullable<GetFinancialAccountQuery['getFinancialAccount']>;
}

/**
 * Detail pane for an investment account. The header's date range drives the value chart and the
 * transactions table, whose activity filter sits in the URL beside the range. The "Current value"
 * change is independent of both: the latest value against the last one recorded on an earlier
 * calendar day. Holdings and RMD tracking are placeholders; the RMD
 * one shows only for a tax-deferred account.
 */
const InvestmentAccountPane = ({ financialAccount }: IInvestmentAccountPaneProps) => {
  const [dateRange, setDateRange] = useDateRangeParams();
  const { activity, setActivity } = useInvestmentActivityParam();
  const zone = getLocalTimeZone();

  const { view, error } = useFinancialAccountBalanceHistory(financialAccount.id, dateRange);

  const latestTimestamp = financialAccount.currentBalance?.timestamp ?? null;
  // Anchored on the latest snapshot's day, not the clock's: a nightly snapshot is stamped the prior evening.
  const priorDayCutoff = React.useMemo(
    () => (latestTimestamp ? previousDayCutoff(new Date(latestTimestamp), zone) : null),
    [latestTimestamp, zone]
  );
  const priorValue = useFinancialAccountBalanceAt(financialAccount.id, priorDayCutoff);

  const currentValue = financialAccount.currentBalance ? parseFloat(financialAccount.currentBalance.balance) : null;
  const hasChange = currentValue !== null && latestTimestamp !== null && priorValue !== null;

  const currentValueCard = (
    <CurrentValueCard
      key="currentValue"
      value={currentValue}
      change={hasChange ? changeBetween(currentValue, priorValue.balance) : null}
      changeLabel={
        hasChange ? sinceDayLabel(new Date(priorValue.timestamp), new Date(latestTimestamp), zone) : undefined
      }
    />
  );

  const summary = (
    <InvestmentAccountSummary
      key="summary"
      subtype={financialAccount.subtype}
      bankName={financialAccount.bank.name}
      bankUrl={financialAccount.bank.url}
    />
  );

  const chart = <ValueOverTimeChart key="chart" view={view} hasError={!!error} />;

  const table = (
    <InvestmentTransactionsTable
      key="transactions"
      financialAccountId={financialAccount.id}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      activity={activity}
      onActivityChange={setActivity}
    />
  );

  const placeholders = [
    <ComingSoonCard key="holdings" title="Holdings">
      You&rsquo;ll see what&rsquo;s inside this account here.
      <br />
      The value above is complete and current.
    </ComingSoonCard>,
    hasRequiredDistributions(financialAccount.subtype) && (
      <ComingSoonCard key="rmd" title="RMD tracking">
        Tax-deferred accounts like this one can have required yearly withdrawals. Mantle will help track them.
      </ComingSoonCard>
    ),
    // Entity.Body wraps every stacked entry in its own gapped div, so an absent card must not reach it.
  ].filter((card): card is React.ReactElement => Boolean(card));

  return (
    <Entity.Pane>
      <AccountPaneHeader financialAccount={financialAccount} dateRange={dateRange} onDateRangeChange={setDateRange} />
      <Entity.Body
        leftColumn={[chart, table, ...placeholders]}
        rightColumn={[currentValueCard, summary]}
        singleColumnOrderOverride={[
          // Sizes to the pane, not the viewport: side by side once both cards fit, stacked below that.
          <div key="overview" className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-4">
            {currentValueCard}
            {summary}
          </div>,
          chart,
          table,
          ...placeholders,
        ]}
      />
    </Entity.Pane>
  );
};

export default InvestmentAccountPane;
